import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import StaticCancellationPoliciesComponent from 'components/Common/CancellationPolicies/Rental/StaticConnectionCancellationPolicies'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineBathIcon from 'LuxKit/Icons/line/LineBathIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineVillaIcon from 'LuxKit/Icons/line/LineVillaIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { nonNullable } from 'lib/array/arrayUtils'
import { getMaxOccupancies } from 'lib/offer/occupancyUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import { getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import { getPropertyCategory } from 'lib/homesAndVillas/getPropertyCategory'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { VILLAS_REVIEWS_TOTAL_THRESHOLD } from 'constants/review'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import Heading from 'LuxKit/Typography/Heading'
import ContactUSP from '../ContactUSP'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import VillasVipServices from './VillasVipServices'
import ContentTagLabels from 'components/Content/ContentTagLabels'

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(24)};
    grid-template-columns: max-content;
    grid-auto-columns: max-content;
    grid-auto-flow: column;
  }
`

interface Props {
  offer: App.VillaOffer
  onViewLocationClick?: () => void
  onViewReviewsClick: () => void
  pkg: App.HotelPackage
  checkIn: string | undefined
  checkOut: string | undefined
  rooms: Array<App.Occupants>
}

function VillasPageIntro(props: Props) {
  const { offer, onViewLocationClick, onViewReviewsClick, checkIn, checkOut, pkg, rooms } = props
  const { property } = offer

  const maxGuests = useMemo(() => {
    const allCapacities = offer.packages.flatMap(p => p.roomRate?.capacities ?? [])
    const max = getMaxOccupancies(allCapacities, { adults: 0 }, {
      maxChildAge: offer.property?.maxChildAge,
      maxInfantAge: offer.property?.maxInfantAge,
      maxTeenagerAge: offer.property?.maxTeenagerAge,
    })
    return max.adults
  }, [offer])

  const propertyCategory = useMemo(() => getPropertyCategory(property.category), [property])
  const location = useMemo(() => nonNullable([offer.locationHeading, offer.locationSubheading]).join(', '), [offer])

  return (
    <Group fullWidth direction="vertical" gap={20} tabletGap={24}>
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={16}>
          <ContentTagLabels offerId={offer.id} page="offer" />
          <OfferPageLabelGroup offer={offer} />
          <VerticalSpacer gap={12}>
            <BodyTextBlock
              data-testid="offer-location"
              variant="small"
              startIcon={<LineMapMarkerIcon />}
            >
              {location} · <TextLink size="small" onClick={onViewLocationClick}>View on map</TextLink>
            </BodyTextBlock>
            <Heading variant="heading2" as="h1">{offer.property.name}</Heading>
          </VerticalSpacer>
        </Group>
        {isOfferRatingDisplayable(property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) && <OfferRating
          link
          inlineLabel
          variant="small"
          rating={property.rating}
          onReviewSubTitleClick={onViewReviewsClick}
        />}
      </Group>
      <Group direction="vertical" gap={12} tabletGap={16}>
        <Divider kind="primary" />
        <SummaryGrid>
          <BodyTextBlock variant="medium" startIcon={<LineVillaIcon />}>
            {propertyCategory}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineUsersAltIcon />}>
            Up to {pluralizeToString('guest', maxGuests)}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineBedIcon />}>
            {pluralizeToString('bedroom', offer.property.bedrooms)}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineBathIcon />}>
            {pluralizeToString('bathroom', offer.property.bathrooms)}
          </BodyTextBlock>
        </SummaryGrid>
        <CSSBreakpoint max="tablet">
          <Group direction="vertical" gap={12} tabletGap={16}>
            <Divider kind="primary" />
            {!checkIn && !pkg.ignoreDynamicCancellationPolicy && <StaticCancellationPoliciesComponent
              cancellationPolicies={pkg.roomRate.cancellationPolicies}
              offerType={offer.type}
            />}
            {checkIn && <TAOCancellationPolicies
              offerId={offer.id}
              useDynamicCancellationPolicies={offer.property.useDynamicCancellationPolicies && !pkg.ignoreDynamicCancellationPolicy}
              timezoneOffset={offer.property.timezoneOffset}
              uniqueKey={pkg.uniqueKey}
              checkInDate={checkIn}
              checkOutDate={checkOut}
              occupants={rooms}
              cancellationPolicy={pkg.roomRate.cancellationPolicy}
              hideExcludesFlights
              offerType={offer.type}
            />}
          </Group>
        </CSSBreakpoint>
        <Divider kind="primary" />
      </Group>
      <VillasVipServices />
      <div>
        <LargeContentToggle maxHeight={200} showChevron showLessText="Read less" showMoreText="Read more">
          <MarkdownRender type="compact" fontSize="medium" content={offer.description} />
        </LargeContentToggle>
      </div>
      <ContactUSP />
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    pkg: getCheapestSelectedPackageOptionWithDefault(appState, state).package,
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    rooms: state.rooms,
  }
}, VillasPageIntro)
